/**
 * Google Analytics utility for tracking events and conversions
 *
 * This module provides a centralized interface for sending events to Google Analytics (GA4).
 * It handles event tracking, conversions, user identification, and page views.
 *
 * GA4 Measurement ID: G-C0Q06C6PQE
 */

/**
 * Check if gtag is available
 * @returns {boolean}
 */
function isGtagAvailable() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/**
 * Track a page view
 * @param {string} path - The page path (e.g., '/dashboard')
 * @param {string} title - The page title
 */
export function trackPageView(path, title) {
  if (!isGtagAvailable()) {
    console.warn('[Analytics] gtag not available, skipping page view tracking');
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

/**
 * Track a custom event
 * @param {string} eventName - The name of the event
 * @param {Object} params - Additional event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!isGtagAvailable()) {
    console.warn(`[Analytics] gtag not available, skipping event: ${eventName}`);
    return;
  }

  window.gtag('event', eventName, params);
}

/**
 * Track user sign-up conversion
 * @param {string} method - Sign-up method (e.g., 'google', 'github')
 * @param {string} userId - User ID (hashed for privacy)
 */
export function trackSignUp(method, userId = null) {
  const params = {
    method,
  };

  if (userId) {
    params.user_id = userId;
  }

  trackEvent('sign_up', params);
}

/**
 * Track user login
 * @param {string} method - Login method (e.g., 'google', 'github')
 */
export function trackLogin(method) {
  trackEvent('login', {
    method,
  });
}

/**
 * Track credit purchase
 * @param {number} value - Purchase amount in USD
 * @param {string} packageType - Package type (e.g., '10', '50', '100', '500', '1000')
 * @param {string} currency - Currency code (default: 'USD')
 */
export function trackPurchase(value, packageType, currency = 'USD') {
  trackEvent('purchase', {
    value,
    currency,
    package_type: packageType,
    transaction_id: `${Date.now()}_${packageType}`, // Simple transaction ID
  });
}

/**
 * Track begin checkout (when user initiates credit purchase)
 * @param {number} value - Purchase amount in USD
 * @param {string} packageType - Package type
 */
export function trackBeginCheckout(value, packageType) {
  trackEvent('begin_checkout', {
    value,
    currency: 'USD',
    package_type: packageType,
  });
}

/**
 * Track job creation
 * @param {string} extractorType - Type of extractor used
 * @param {number} estimatedCost - Estimated cost in USD
 */
export function trackCreateJob(extractorType, estimatedCost = null) {
  const params = {
    extractor_type: extractorType,
  };

  if (estimatedCost !== null) {
    params.estimated_cost = estimatedCost;
  }

  trackEvent('create_job', params);
}

/**
 * Track API key creation
 */
export function trackCreateApiKey() {
  trackEvent('create_api_key');
}

/**
 * Track CTA button click
 * @param {string} ctaText - Text of the CTA button (e.g., 'Get Started', 'Try Free')
 * @param {string} location - Location of the CTA (e.g., 'hero', 'pricing', 'footer')
 */
export function trackCtaClick(ctaText, location) {
  trackEvent('cta_click', {
    cta_text: ctaText,
    location,
  });
}

/**
 * Track cost calculator interaction
 * @param {string} action - Action taken (e.g., 'duration_change', 'extractor_change', 'calculate')
 */
export function trackCostCalculator(action) {
  trackEvent('cost_calculation', {
    action,
  });
}

/**
 * Track pricing package view
 * @param {string} packageType - Package type viewed
 */
export function trackPricingPackageView(packageType) {
  trackEvent('pricing_package_view', {
    package_type: packageType,
  });
}

/**
 * Track developer docs view
 * @param {string} section - Section of docs viewed (if applicable)
 */
export function trackViewDocs(section = null) {
  const params = {};
  if (section) {
    params.section = section;
  }
  trackEvent('view_docs', params);
}

/**
 * Set user ID for tracking authenticated users
 * @param {string} userId - User ID (should be hashed for privacy)
 */
export function setUserId(userId) {
  if (!isGtagAvailable()) {
    console.warn('[Analytics] gtag not available, skipping user ID setting');
    return;
  }

  window.gtag('set', {
    user_id: userId,
  });
}

/**
 * Set user properties
 * @param {Object} properties - User properties to set
 */
export function setUserProperties(properties) {
  if (!isGtagAvailable()) {
    console.warn('[Analytics] gtag not available, skipping user properties');
    return;
  }

  window.gtag('set', 'user_properties', properties);
}

/**
 * Track user authentication status
 * @param {boolean} isAuthenticated - Whether user is authenticated
 */
export function trackUserType(isAuthenticated) {
  setUserProperties({
    user_type: isAuthenticated ? 'authenticated' : 'anonymous',
  });
}
